import type { NativeSelectOption } from '@shared/ui/fields'
import type { DisciplineDeliveryResult, DisciplinePaymentProduct } from './interfaces'

import { Discipline } from '@brighterly/lib-core-types/discipline/enums'

import { formatPrice } from '@shared/lib/format'

import {
  DisciplineDeliveryStatus,
  DisciplineMessageSender,
  DisciplinePaymentOption,
  DisciplinePaymentProductType,
} from './enums'

interface DisciplinePaymentProducts {
  oneTime: DisciplinePaymentProduct[]
  subscriptions: DisciplinePaymentProduct[]
}

interface DisciplineDeliverySummary {
  delivered: string[]
  problems: string[]
}

const PAYMENT_EMPTY_VALUE = '---'

const WITHOUT_DISCOUNT = 'Without discount'

export const MESSAGE_SENDER_LABELS: Record<DisciplineMessageSender, string> = {
  [DisciplineMessageSender.Email]: 'Email',
  [DisciplineMessageSender.Sms]: 'SMS',
  [DisciplineMessageSender.Whatsapp]: 'Whatsapp',
}

const DELIVERY_REASON_LABELS: Record<string, string> = {
  brand: 'not sent for this brand',
  wa_opt_out: 'the customer opted out of WhatsApp',
  no_contact: 'no contact on file',
  otp_skip_codes: 'test or internal number',
  provider_error: 'the provider refused it',
}

/** The server does not match products to the subject: an ELA discipline gets the rows with ELA lessons, MATH the rest. */
export const splitDisciplinePaymentProducts = (
  products: DisciplinePaymentProduct[],
  code: Discipline | null,
): DisciplinePaymentProducts => {
  const isEla = code === Discipline.ELA
  const forSubject = products.filter(product => isEla ? product.lessons.ela > 0 : product.lessons.ela === 0)

  return {
    oneTime: forSubject.filter(product => product.type === DisciplinePaymentProductType.OneTime),
    subscriptions: forSubject.filter(product => product.type === DisciplinePaymentProductType.Subscription),
  }
}

export const getPaymentProductLessons = (product: DisciplinePaymentProduct | null, code: Discipline | null): number | null => {
  if (!product) return null

  return code === Discipline.ELA ? product.lessons.ela : product.lessons.math
}

export const toPaymentProductOptions = (
  products: DisciplinePaymentProduct[],
  selectedId: number | null,
): NativeSelectOption[] => products.map(product => ({
  id: product.id,
  value: product.id,
  text: `${product.name} - ${formatPrice(product.price)}`,
  selected: product.id === selectedId,
}))

/** 0 ('Without discount') is always accepted, so it leads the list whether or not it is configured. */
export const toPaymentDiscountOptions = (discounts: number[], selected: number): NativeSelectOption[] => {
  const percents = [0, ...discounts.filter(discount => discount !== 0)]

  return percents.map(percent => ({
    id: percent,
    value: percent,
    text: percent === 0 ? WITHOUT_DISCOUNT : `${percent}%`,
    selected: percent === selected,
    colorText: percent === 0 ? 'gray' : '',
  }))
}

/** Legacy always started on installments. Only an admin who cannot create installments but can create a one-time
 *  order starts on the option they can use. */
export const getDefaultPaymentOption = (canChooseInstallment: boolean, canChooseOneTime: boolean): DisciplinePaymentOption =>
  !canChooseInstallment && canChooseOneTime ? DisciplinePaymentOption.OneTime : DisciplinePaymentOption.Installment

/** A discount shown as an amount to subtract, or nothing when there is no price to discount. */
export const formatPaymentDiscountAmount = (price: number | null, discountUsd: number | null): string =>
  price && discountUsd && discountUsd > 0 ? `-${formatPrice(discountUsd)}` : ''

export const formatPaymentValue = (value: number | null | undefined): string =>
  value ? String(value) : PAYMENT_EMPTY_VALUE

export const formatPaymentPrice = (value: number | null | undefined): string =>
  formatPrice(value ?? 0)

/** A 200 can mean that nothing went out, so every channel is reported, delivered or not. */
export const summarizeDeliveries = (results: DisciplineDeliveryResult[]): DisciplineDeliverySummary => {
  const isDelivered = (result: DisciplineDeliveryResult): boolean =>
    result.status === DisciplineDeliveryStatus.Sent || result.status === DisciplineDeliveryStatus.Queued

  return {
    delivered: results.filter(isDelivered).map(result => MESSAGE_SENDER_LABELS[result.channel] ?? result.channel),
    problems: results.filter(result => !isDelivered(result)).map((result) => {
      const channel = MESSAGE_SENDER_LABELS[result.channel] ?? result.channel
      const reason = result.reason ? DELIVERY_REASON_LABELS[result.reason] ?? result.reason : result.status

      return `${channel}: ${reason}`
    }),
  }
}
